import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    PLATFORM_ID,
    provideZoneChangeDetection,
    REQUEST,
    TransferState
} from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { ApolloClient } from '@apollo/client';
import { provideTransloco } from '@jsverse/transloco';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { providePrimeNG } from 'primeng/config';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { apolloOptionsFactory } from './core/apollo-client-provider';
import { DefaultInterceptor } from './core/providers/data/interceptor';
import { TranslocoHttpLoader } from './transloco-loader';

export interface ServerConfig {
    apiHost: string;
    apiPort: number;
    shopApiPath: string;
}
declare const global: any;
export const GLOBAL_APP_CONFIG_KEY = '__storefront_app_config';
let serverConfig: ServerConfig | undefined;

/**
 * Loads the app config from the JSON file via a browser-side fetch call.
 */
export function loadAppConfig(): Promise<void> {
    return fetch('./storefront-config.json')
        .then((res) => res.json())
        .then((config) => {
            serverConfig = config;
        });
}

/**
 * Loads the app config from the server-side global object
 */
export function loadAppConfigServer(): void {
    serverConfig = global[GLOBAL_APP_CONFIG_KEY];
}

export function getAppConfig(): ServerConfig {
    if (!serverConfig) {
        loadAppConfigServer();
    }
    if (!serverConfig) {
        throw new Error(`server config not loaded`);
    }
    return serverConfig;
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes, withInMemoryScrolling({
            scrollPositionRestoration: 'top',
            anchorScrolling: 'enabled',
        })),
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        provideClientHydration(),
        providePrimeNG({
            theme: {
                preset: environment.primeNgPreset
            }
        }),
        { provide: HTTP_INTERCEPTORS, useClass: DefaultInterceptor, multi: true },
        { provide: APP_BASE_HREF, useValue: environment.baseHref },
        // Ensure HttpLink is available even without an NgModule
        { provide: HttpLink, useFactory: () => new HttpLink(inject(HttpClient)) },
        provideApollo(
            () =>
                new ApolloClient(
                    apolloOptionsFactory(inject(HttpLink), inject(PLATFORM_ID), inject(TransferState), inject<any>(REQUEST, { optional: true }))
                )
        ),
        provideTransloco({
            config: {
                availableLangs: ['en', 'de'],
                defaultLang: 'de',
                fallbackLang: 'en',
                // Remove this option if your application doesn't support changing language in runtime.
                reRenderOnLangChange: true,
                prodMode: !isDevMode()
            },
            loader: TranslocoHttpLoader
        })
    ]
};
